import * as React from 'react'
import { styled } from 'next-yak'

import { t, tc } from '@owl-nest/localize'
import { Messages } from '@owl-nest/redux-wrapper/src/messages/reducer'
import * as model from '@owl-nest/models'
import { Project, ProjectSelfPermission } from '@owl-nest/api-client/latest'
import { BREAKPOINTS, COLORS, glyphs, Link, styles as S } from '@ulule/owl-kit-components/next'

const Content = styled.div`
  ${S.copy.S.regular} {
    text-align: center;
  }

  ${Link} {
    margin-left: 3px;
    font-weight: inherit;
  }
`
const Info = styled.div`
  align-items: center;
  display: flex;
  justify-content: center;
  margin-bottom: 4px;

  ${S.heading.XXXXS} {
    color: ${COLORS.PRIMARY_GREY_900};
  }

  ${glyphs.Icon} {
    margin-right: 7px;
  }

  @media screen and ${BREAKPOINTS.LAPTOP} {
    margin-bottom: 0px;
  }
`

const Mobile = styled.div`
  display: block;

  ${S.copy.S.regular} {
    text-align: center;
  }

  @media screen and ${BREAKPOINTS.LAPTOP} {
    display: none;
  }
`

export function getMessages(project: Project): Messages {
  // USER & CLIENT STATE
  const userPermissions = project.permissions.self
  const userCanEdit = userPermissions?.includes(ProjectSelfPermission.UPDATE)
  const isMobile = !window.matchMedia(BREAKPOINTS.LAPTOP).matches

  // PROJECT STATE
  const isPendingUser = project.status === 'pending-owner'
  const isFinished = model.project.isFinished(project)
  const isExtraTime = project.is_in_extra_time
  const isFunded = project.goal_raised && isFinished
  const userCanAccessBilling = project.billing !== undefined
  const projectHadExtraTime = model.project.hadExtraTime(project)

  const messages = {
    ['pending-changes']: {
      context: 'pending-changes',
      level: 'info',
      message: [
        <div key={'pending-changes-left-slot'}>
          <Info>
            <S.heading.XXXXS>{t('Awaiting modifications')}</S.heading.XXXXS>
          </Info>
        </div>,
        <div key={'pending-changes-center-slot'}>
          <Content>
            <S.copy.S.regular>
              {tc(
                'Your project is awaiting your modifications if you need help or if your project is ready [link: contact the team]',
                {
                  link: <Link kind="primary" underlined href={`/projects/${project.id}/discussion/`} />,
                },
              )}
            </S.copy.S.regular>
          </Content>
        </div>,
      ],
    },
    ['extratime']: {
      context: 'extratime',
      level: 'success',
      message: [
        <div key={'extratime-left-slot'}>
          <Info>
            <S.heading.XXXXS>{t('Extended')}</S.heading.XXXXS>
          </Info>
        </div>,
        <div key={'extratime-center-slot'}>
          <Content>
            <S.copy.S.regular>
              {t(
                'Congratulations, your project is funded and extended! You will receive your first funds in the next few days and people can continue to back your project.',
              )}
            </S.copy.S.regular>
          </Content>
        </div>,
      ],
    },
    ['extratime_billing']: {
      context: 'extratime_billing',
      level: 'success',
      message: [
        <div key={'extratime-billing-left-slot'}>
          <Info>
            <S.heading.XXXXS>{t('Extended')}</S.heading.XXXXS>
          </Info>
        </div>,
        <div key={'extratime-billing-center-slot'}>
          <Content>
            <S.copy.S.regular>
              {t(
                'Congratulations, your project is funded and extended! You will receive your first funds in the next few days and people can continue to back your project.',
              )}
              {userCanAccessBilling && project.billing?.invoice && (
                <Link href={project?.billing?.invoice.url} kind="primary" underlined>
                  {t('Download first invoice.')}
                </Link>
              )}
            </S.copy.S.regular>
          </Content>
        </div>,
      ],
    },
    ['extratime_ended']: {
      context: 'extratime_ended',
      level: 'success',
      message: [
        <div key={'extratime-ended-left-slot'}>
          <Info>
            <S.heading.XXXXS>{t('Successfully funded')}</S.heading.XXXXS>
          </Info>
        </div>,
        <div key={'extratime-ended-center-slot'}>
          <Content>
            <S.copy.S.regular>
              {t(
                'Congratulations, your project is successfully funded, you will receive the funds of your extension period in the next few days, stay in touch with our team.',
              )}
            </S.copy.S.regular>
          </Content>
        </div>,
      ],
    },
    ['funded']: {
      context: 'funded',
      level: 'success',
      message: [
        <div key={'funded-left-slot'}>
          <Info>
            <S.heading.XXXXS>{t('Successfully funded')}</S.heading.XXXXS>
          </Info>
        </div>,
        <div key={'funded-center-slot'}>
          <Content>
            <S.copy.S.regular>
              {t(
                'Congratulations, your project is successfully funded, you will receive your funds in the next few days, stay in touch with our team.',
              )}
            </S.copy.S.regular>
          </Content>
        </div>,
      ],
    },
    ['funded_billing']: {
      context: 'funded_billing',
      level: 'success',
      message: [
        <div key={'funded-billing-left-slot'}>
          <Info>
            <S.heading.XXXXS>{t('Successfully funded')}</S.heading.XXXXS>
          </Info>
        </div>,
        <div key={'funded-billing-center-slot'}>
          <Content>
            <S.copy.S.regular>
              {t(
                'Congratulations, your project is successfully funded, you will receive your funds in the next few days, stay in touch with our team.',
              )}
              {userCanAccessBilling && project.billing?.invoice && (
                <Link href={project?.billing?.invoice.url} kind="primary" underlined>
                  {t('Download your invoice.')}
                </Link>
              )}
            </S.copy.S.regular>
          </Content>
        </div>,
      ],
    },
    ['mobile']: {
      context: 'mobile',
      level: 'info',
      message: (
        <Mobile>
          <S.copy.S.regular>
            {t(
              'To enjoy all our features to create a great project page, we strongly suggest you to do it on a computer!',
            )}
          </S.copy.S.regular>
        </Mobile>
      ),
    },
  }

  type keys =
    | 'pending-changes'
    | 'extratime'
    | 'extratime_billing'
    | 'extratime_ended'
    | 'funded'
    | 'funded_billing'
    | 'mobile'
  let keysArray: keys[] = []

  if (userCanEdit) {
    // Mobile : presale isn't finished
    if (isMobile && !isFinished) {
      keysArray = [...keysArray, 'mobile']
    }

    // Project is finished and funded
    if (isFinished && isFunded) {
      if (projectHadExtraTime) {
        //Project had extratime
        keysArray = [...keysArray, 'extratime_ended']
      } else if (userCanAccessBilling) {
        // No extratime and funding with billing
        keysArray = [...keysArray, 'funded_billing']
      } else {
        // No extratime and funded without billing
        keysArray = [...keysArray, 'funded']
      }
    }

    // Extra-time and first billing done
    if (isExtraTime && userCanAccessBilling) {
      keysArray = [...keysArray, 'extratime_billing']
    } else if (isExtraTime) {
      // Extra-time and first billing not done yet
      keysArray = [...keysArray, 'extratime']
    }

    // Pending user update
    if (isPendingUser) {
      keysArray = [...keysArray, 'pending-changes']
    }
  }

  const filteredMessages = keysArray.reduce((filteredMessages, message) => {
    filteredMessages = { ...filteredMessages, [message]: messages[message] }
    return filteredMessages
  }, {})

  return Object.values(filteredMessages)
}
